<template>
    <div class="bid-packages" :class="{'landing': layoutType === 'landing'}" v-if="projectRouteId && project && bidPackages.length">
      <div v-if="isBidPackageContext" class="label has-margin-left bid-packages-project-title">{{ projectMenuTitle }}</div>
      <div class="label" :class="{'has-margin-left': layoutType !== 'landing'}">Bid Packages</div>
      <div class="bid-packages-list">
        <template v-if="layoutType === 'landing'">
          <p class="landing-intro">This project is structured in bid packages. You can access the following bid packages.</p>
          <div class="bid-package-grid">
          <div class="box bid-package-card" v-if="hasProjectLevelView" @click="handleSelected('project')">
            <div class="bid-package-card-header" :title="project.name">{{ project.name }}</div>
            <div class="bid-package-card-body">
              <div class="bid-package-detail">
                <span class="label">Bid Date</span>
                <span>{{ formatBidDueDate(project.sub_bid_date) }}</span>
              </div>
              <div class="bid-package-detail">
                <span class="label">Bid Time</span>
                <span>{{ formatBidDueTime(project.sub_bid_time) }}</span>
              </div>
              <div class="bid-package-detail">
                <span class="label">Bidding</span>
                <span class="tag" :class="biddingTagClass(project)">{{ isBidding(project) }}</span>
              </div>
              <div class="bid-package-detail">
                <span class="label">Bid Uploaded</span>
                <span class="tag" :class="bidUploadedTagClass(project)">{{ isBidUploaded(project) }}</span>
              </div>
            </div>
            <div class="bid-package-card-footer">
              <button class="button is-fullwidth bid-package-card-cta" type="button" @click.stop="handleSelected('project')">View Bid Package</button>
            </div>
          </div>
          <div class="box bid-package-card" v-for="bidPackage in bidPackages" :key="bidPackage.id" @click="handleSelected(bidPackage.id)">
            <div class="bid-package-card-header" :title="bidPackage.name">{{ bidPackage.name }}</div>
            <div class="bid-package-card-body">
              <div class="bid-package-detail">
                <span class="label">Bid Date</span>
                <span>{{ formatBidDueDate(bidPackage.sub_bid_date) }}</span>
              </div>
              <div class="bid-package-detail">
                <span class="label">Bid Time</span>
                <span>{{ formatBidDueTime(bidPackage.sub_bid_time) }}</span>
              </div>
              <div class="bid-package-detail">
                <span class="label">Bidding</span>
                <span class="tag" :class="biddingTagClass(bidPackage)">{{ isBidding(bidPackage) }}</span>
              </div>
              <div class="bid-package-detail">
                <span class="label">Bid Uploaded</span>
                <span class="tag" :class="bidUploadedTagClass(bidPackage)">{{ isBidUploaded(bidPackage) }}</span>
              </div>
            </div>
            <div class="bid-package-card-footer">
              <button class="button is-fullwidth bid-package-card-cta" type="button" @click.stop="handleSelected(bidPackage.id)">View Bid Package</button>
            </div>
          </div>
          </div>
        </template>
        <template v-else>
            <o-menu class="menu">
              <template v-if="hasProjectLevelView">
                <o-menu-item :label="project.name"
                             class="menu-item bid-package-menu-item"
                             :class="{'active': isActive('project')}"
                             @click="handleSelected('project')"
                />
                <NavMenu v-if="isActive('project') && projectTabsMenu" class="bid-package-tabs-menu" :menu="projectTabsMenu" />
              </template>
              <template v-for="bidPackage in bidPackages" :key="bidPackage.id">
                <o-menu-item :label="bidPackage.name"
                             icon="cube"
                             class="menu-item bid-package-menu-item"
                             :class="{'active': isActive(bidPackage.id)}"
                             @click="handleSelected(bidPackage.id)"
                />
                <NavMenu secondary v-if="isActive(bidPackage.id) && projectTabsMenu" class="bid-package-tabs-menu" :menu="projectTabsMenu" />
              </template>
            </o-menu>
        </template>
      </div>
    </div>
</template>

<script setup lang="ts">

import {useRoute, useRouter} from "vue-router";
import { storeToRefs } from 'pinia';

import {computed, toRefs, watch} from "vue";
import type {BidPackage} from "@/models/BidPackage";
import type {Project} from "@/models/Project";
import {useProjectStore} from "@/stores/ProjectStore";
import NavMenu from "@/components/NavMenu.vue";
import {Menu} from "@/internal";
import {getDateMask} from '@/use/culture';
import formatRFQDateTime, {parseRFQDateTime} from '@/use/formatRFQDateTime';

const projectStore = useProjectStore();
const route = useRoute();
const router = useRouter();
const { project } = storeToRefs(useProjectStore());

type LayoutTypes = "menu" | "landing";

const props = withDefaults(defineProps<{
  dark?: boolean,
  layoutType?: LayoutTypes,
  projectTabsMenu?: Menu | null,
}>(), {
  dark: false,
  layoutType: "menu",
  projectTabsMenu: null,
});
const {
  dark,
  layoutType,
  projectTabsMenu,
} = toRefs(props);

const bidders = computed(() => project.value?.bidders || []);

const bidPackages = computed(() => [...(project.value?.bid_packages || [])].sort((left, right) =>
  (left.name ?? '').localeCompare(right.name ?? '', undefined, {
    numeric: true,
    sensitivity: 'base',
  })
));

// A project-level invitation is only actionable when there are no accessible
// bid packages. Once packages are present, the bidder must work within one of
// those packages; rendering both navigations creates an invalid mixed view.
const hasProjectLevelView = computed(() => bidders.value.length > 0 && bidPackages.value.length === 0);

const projectRouteId = computed(() => String(route.params.projectId ?? ''));

const isPastProjectRoute = computed(() => String(route.name).startsWith('projects/past'));
const projectRouteName = computed(() => isPastProjectRoute.value ? 'projects/past/show' : 'projects/show');
const bidPackageRouteName = computed(() => isPastProjectRoute.value ? 'projects/past/bid-packages/show' : 'projects/bid-packages/show');

const selectedPackage = computed<number | string | null>(() => {
  if (typeof route.params.bidPackageId === 'string') {
    return parseInt(route.params.bidPackageId);
  }

  return projectStore.viewBidPackageId === 0 ? 'project' : null;
});

const isBidPackageContext = computed(() => layoutType.value === 'menu' && typeof route.params.bidPackageId === 'string');

const projectMenuTitle = computed(() => {
  const projectName = project.value?.name?.trim() ?? '';
  const bidPhaseName = project.value?.bid_phase_name?.trim();

  return bidPhaseName ? `${projectName} (${bidPhaseName})` : projectName;
});

function isActive(value: number | string | null) {
  return selectedPackage.value === value;
}

function isBidding(obj: BidPackage | Project) {
  return obj.is_bidding === 1 ? "Yes" : (obj.is_bidding === 2 ? 'No' : (obj.is_bidding === 3 ? 'Unsure' : 'Unconfirmed'));
}

function isBidUploaded(obj: BidPackage | Project) {
  return obj.is_bid_uploaded ? 'Yes' : 'No';
}

function formatBidDueDate(value: string | null | undefined) {
  const dateTime = parseRFQDateTime(value);

  return dateTime ? dateTime.toFormat(getDateMask('long')) : 'Not specified';
}

function formatBidDueTime(time: string | null | undefined) {
  if (!time) {
    return 'Not specified';
  }

  return formatRFQDateTime('', time);
}

function biddingTagClass(obj: BidPackage | Project) {
  return {
    'is-success': obj.is_bidding === 1,
    'is-danger': obj.is_bidding === 2,
    'is-warning': obj.is_bidding === 3,
    'is-light': !obj.is_bidding,
  };
}

function bidUploadedTagClass(obj: BidPackage | Project) {
  return obj.is_bid_uploaded ? 'is-success' : 'is-light';
}

function handleSelected(value: number | string | null) {
  if (value === 'project') {
    router.push({
      name: projectRouteName.value,
      params: { projectId: projectRouteId.value },
      hash: route.hash,
    });
    return;
  }

  router.push({
    name: bidPackageRouteName.value,
    params: { projectId: projectRouteId.value, bidPackageId: value },
    hash: route.hash,
  });
}

watch(project, () => {
  // This component also lives in the global/mobile navigation. The project
  // store can briefly retain the previously viewed project after navigating
  // back to an index route, where there is no projectId to build a route with.
  if (!projectRouteId.value) {
    return;
  }

  if (!bidders.value.length && bidPackages.value.length === 1) {
    if (route.params.bidPackageId !== String(bidPackages.value[0].id)) {
      router.replace({
        name: bidPackageRouteName.value,
        params: { projectId: projectRouteId.value, bidPackageId: bidPackages.value[0].id },
        hash: route.hash,
      });
    }
  }
  else if (bidders.value.length && !bidPackages.value.length) {
    if (route.name !== projectRouteName.value) {
      router.replace({ name: projectRouteName.value, params: { projectId: projectRouteId.value }, hash: route.hash });
    }
  }
}, { immediate: true });

</script>

<style lang="scss" scoped>

.bid-packages {
  &.landing {
    width: unset !important;

    .landing-intro {
      margin: .5rem 0 1.25rem;
      color: $grey-dark;
    }

    .bid-package-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(18rem, 1fr));
      gap: 1rem;
      max-width: 56rem;
    }

    .bid-package-card {
      display: flex;
      flex-direction: column;
      margin: 0;
      min-height: 10.5rem;
      overflow: hidden;
      padding: 0;

      .bid-package-card-header {
        background-color: var(--bid-package-card-background);
        color: var(--bid-package-accent-text);
        font-size: 1.1rem;
        font-weight: 600;
        overflow: hidden;
        padding: 1rem;
        text-align: center;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .bid-package-card-body {
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: .65rem;
        padding: 1rem;
      }

      .bid-package-card-footer {
        padding: 0 1rem 1rem;
      }

      .bid-package-card-cta {
        background-color: var(--bid-package-card-background);
        border-color: var(--bid-package-card-border-color);
        color: var(--bid-package-accent-text);

        &:hover,
        &:focus-visible {
          background-color: var(--bid-package-card-background-hover);
          border-color: var(--bid-package-card-border-color-hover);
          color: var(--bid-package-accent-text);
        }
      }

      .bid-package-detail {
        align-items: center;
        display: flex;
        gap: .75rem;
        justify-content: space-between;

        .label {
          margin: 0;
          color: var(--bid-package-card-text);
        }

        .tag {
          font-weight: 700;
          margin: 0;
        }
      }
    }
  }

  .bid-packages-list {
    .box {
      &:hover {
        border-color: $primary;
      }

      &:hover {
        cursor: pointer;
      }

      .column {
        white-space: nowrap;
      }
    }
  }
}

</style>

<style lang="scss">

.bid-packages {
  .label {
    color: var(--nav-secondary-text);
  }

  .bid-packages-project-title {
    display: block;
    font-weight: 600;
    font-size: 1.05rem;
    padding-bottom: .25rem;
    margin-bottom: .75rem;
    border-bottom: 2px solid var(--nav-border-color);
    border-bottom-color: var(--nav-secondary-border-color);
  }

  .bid-packages-list {
    .menu {
      border-left: 1px solid var(--nav-border-color);
      border-left-color: var(--nav-secondary-border-color);
      margin: 0 0 0 1em;
      padding-left: .5em;

      li {
        list-style-type: none;
      }

      .menu-item {
        margin-top: 3px;
        margin-bottom: 3px;
        padding: .5em .75em;
        display: flex;
        align-items: center;
        color: unset;
        border: 1px solid transparent;

        span:nth-child(2) {
          margin-left: .3em;
        }

        &.bid-package-menu-item{
          color: var(--nav-secondary-text);

          &:hover {
            background-color: var(--nav-secondary-background-hover);
          }

          &.active,
          &:hover:not(.has-ps-icon):not(.has-image),
          &.router-link-exact-active:not(.has-ps-icon):not(.has-image) {
            background: var(--nav-secondary-background-active);
            color: var(--nav-secondary-text);

            &:hover:not(.has-ps-icon):not(.has-image) {
              background: var(--nav-secondary-background-hover);
              color:var(--nav-secondary-text);
            }
          }
        }
      }

      .bid-package-tabs-menu {
        background: transparent;
        border-left: 1px solid var(--nav-border-color);
        border-left-color: var(--nav-secondary-border-color);
        margin: 0 0 .5em 1.25em;
        padding: .25em 0 .25em .5em;

        .menu-item {
          padding: .45em .75em;
        }
      }

    }
  }
}

</style>
